/** Where a node of the neural field is. */
export interface NodePosition {
  x: number;
  y: number;
}

/**
 * Whether the links between nodes, which join any two closer than `linkDistance`, tie every node into one network: a flood fill from
 * the first node along those links. The pointer's own lines are not links and never count.
 *
 * @param nodes - The nodes' positions.
 * @param linkDistance - How close two nodes must be to be linked.
 * @returns True when every node can be reached from every other along links; false for an empty field.
 */
export function linksIntoOneNetwork(nodes: readonly NodePosition[], linkDistance: number): boolean {
  if (nodes.length === 0) {
    return false;
  }

  const reached = new Array<boolean>(nodes.length).fill(false);
  const pending = [0];
  reached[0] = true;
  let count = 1;

  for (let index = pending.pop(); index !== undefined; index = pending.pop()) {
    const from = nodes[index];
    for (let other = 0; other < nodes.length; other += 1) {
      if (!reached[other] && Math.hypot(nodes[other].x - from.x, nodes[other].y - from.y) < linkDistance) {
        reached[other] = true;
        count += 1;
        pending.push(other);
      }
    }
  }

  return count === nodes.length;
}
